(() => {
	'use strict';
	const ROOT_SELECTOR = '[data-buybox-root]';
	const MOBILE_QUERY = '(min-width: 768px)';
	const CADENCE_CONTROL_SELECTOR = ['[data-action="draft-cadence"]', '[data-selling-plan-id]', '[data-plan-id]', '[data-selling-plan]', 'input[type="radio"]', 'option'].join(',');
	const toInt = (value, fallback = 0) => {
		const number = Number.parseInt(value, 10);
		return Number.isFinite(number) ? number : fallback;
	};
	const normalizePlanId = (value) => {
		if (!value) return '';
		const match = String(value).match(/(?:SellingPlan\/)?(\d{5,})$/);
		return match ? match[1] : '';
	};
	const parseCadence = (value) => {
		if (!value) return null;
		const text = String(value).trim().toLowerCase();
		const match = text.match(/\b(\d+)\b/);
		const count = Math.max(1, match ? Number(match[1]) : 1);
		if (/\byears?\b/.test(text)) return { count, unit: 'year' };
		if (/\bweeks?\b/.test(text)) return { count, unit: 'week' };
		if (/\bdays?\b/.test(text)) return { count, unit: 'day' };
		if (/\bmonths?\b/.test(text) || text.includes('monthly')) return { count, unit: 'month' };
		return null;
	};
	const addMonths = (date, months) => {
		const result = new Date(date);
		const day = result.getDate();
		result.setDate(1);
		result.setMonth(result.getMonth() + months);
		const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
		result.setDate(Math.min(day, lastDay));
		return result;
	};
	const nextDateFromCadence = (cadence, from = new Date()) => {
		if (!cadence) return null;
		if (cadence.unit === 'month') return addMonths(from, cadence.count);
		if (cadence.unit === 'year') return addMonths(from, cadence.count * 12);
		const result = new Date(from);
		const days = cadence.unit === 'week' ? cadence.count * 7 : cadence.count;
		result.setDate(result.getDate() + days);
		return result;
	};
	const deliveriesPerYear = (cadence) => {
		if (!cadence) return null;
		const divisors = {
			month: 12,
			week: 52,
			day: 365,
			year: 1,
		};
		const value = divisors[cadence.unit] / cadence.count;
		if (!Number.isFinite(value) || value <= 0) return null;
		return Number.isInteger(value) ? value : Math.round(value * 10) / 10;
	};
	const cadenceDays = (cadence) => {
		if (!cadence) return null;
		const multipliers = {
			day: 1,
			week: 7,
			month: 365.25 / 12,
			year: 365.25,
		};
		return cadence.count * (multipliers[cadence.unit] || 0) || null;
	};
	const formatDate = (date) =>
		date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	const setText = (element, value) => {
		if (!element) return;
		const next = String(value);
		if (element.textContent !== next) element.textContent = next;
	};
	const setHidden = (element, hidden) => {
		if (element && element.hidden !== hidden) element.hidden = hidden;
	};
	const replaceChildrenCompat = (element, ...children) => {
		if (!element) return;
		while (element.firstChild) element.removeChild(element.firstChild);
		children.forEach((child) => {
			if (child == null) return;
			element.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
		});
	};
	const escapeCssValue = (value) => {
		const text = String(value || '');
		if (window.CSS && typeof window.CSS.escape === 'function') {
			return window.CSS.escape(text);
		}
		return text.replace(/([ !"#$%&'()*+,./:;<=>?@[\\]^`{|}~])/g, '\\$1');
	};
	class NuBestBuybox {
		constructor(root) {
			var _a;
			this.root = root;
			this.form = root.querySelector('form[data-buybox]');
			if (!this.form) return;
			this.cleanupFns = [];
			this.rafIds = new Set();
			this.timeouts = new Set();
			this.cacheStaticNodes();
			if (!this.rows.length) return;
			this.variantOptions = new Map(this.select ? Array.from(this.select.options, (option) => [String(option.value), option]) : []);
			this.rowCache = new WeakMap();
			this.rows.forEach((row) => this.rowCache.set(row, this.buildRowCache(row)));
			const selected = this.rows.find((row) => row.classList.contains('is-selected')) || this.rows[0];
			this.lastPlanId = ((_a = this.planInput) === null || _a === void 0 ? void 0 : _a.value) || '';
			this.state = {
				purchase: this.form.dataset.purchase === 'onetime' ? 'onetime' : 'sub',
				row: selected,
				editingCadence: false,
				deliverySource: 'control',
			};
			this.bind();
			this.enableStickyTracking();
			if (this.quickShopRoot) {
				this.bindQuickShop();
				this.renderCta();
			}
		}
		cacheStaticNodes() {
			this.select = this.form.querySelector('[data-buybox-select]');
			this.planInput = this.form.querySelector('[data-buybox-plan]');
			this.qtyInput = this.form.querySelector('[data-buybox-qty]');
			this.cta = this.form.querySelector('[data-buybox-cta]');
			this.sticky = this.root.querySelector('[data-buybox-sticky]');
			this.delivery = this.form.querySelector('[data-delivery]');
			this.cadenceList = this.form.querySelector('[data-cadence-list]');
			this.rows = Array.from(this.form.querySelectorAll('.bb-row'));
			this.quickShopRoot = this.root.closest('.main-qs');
			this.ui = {
				purchaseButtons: Array.from(this.root.querySelectorAll('[data-action="draft-purchase"]')),
				variantButtons: Array.from(this.root.querySelectorAll('[data-action="variant"]')),
				hintWrap: this.root.querySelector('.bb-hint'),
				hint: this.root.querySelector('.bb-meta [data-hint]'),
				nudge: this.root.querySelector('[data-nudge]'),
				doctor: this.root.querySelector('[data-doctor]'),
				disclosure: this.root.querySelector('[data-disclosure]'),
				stickyCta: this.root.querySelector('[data-sticky-cta]'),
			};
			this.deliveryUi = this.delivery
				? {
						toggle: this.delivery.querySelector('[data-action="toggle-cadence"]'),
						cadenceText: this.delivery.querySelector('[data-cadence-text]'),
						cadenceStrong: this.delivery.querySelector('[data-cadence-text] strong'),
						next: this.delivery.querySelector('[data-next]'),
						mismatch: this.delivery.querySelector('[data-mismatch]'),
					}
				: null;
			this.ctaUi = this.cta
				? (() => {
						const wrap = this.cta.querySelector('.bb-cta__label');
						let label = null;
						if (wrap) {
							label = wrap.firstElementChild;
						}
						let total = null;
						if (wrap) {
							total = wrap.lastElementChild;
						}
						return {
							wrap,
							label,
							separator: (wrap || this.cta).querySelector('.bb-cta__sep'),
							compare: (wrap || this.cta).querySelector('.bb-cta__compare'),
							total,
						};
					})()
				: null;
			this.stickyUi = this.sticky
				? {
						total: this.sticky.querySelector('[data-sticky-total]'),
						sub: this.sticky.querySelector('[data-sticky-sub]'),
						label: this.sticky.querySelector('[data-sticky-label]'),
						button: this.sticky.querySelector('[data-sticky-cta]'),
					}
				: null;
		}
		buildRowCache(row) {
			var _a, _b, _c, _d, _e, _f, _g;
			const q = (selector) => row.querySelector(selector);
			return {
				button: q('.bb-row__btn'),
				details: q('.bb-row__details'),
				save: q('[data-save]'),
				sub: q('[data-sub]'),
				total: q('[data-total]'),
				compare: q('[data-compare]'),
				note: q('[data-note]'),
				cadenceTemplate: q('[data-row-cadence-template]'),
				original: {
					saveText: ((_a = q('[data-save]')) === null || _a === void 0 ? void 0 : _a.textContent) || '',
					saveHidden: Boolean((_b = q('[data-save]')) === null || _b === void 0 ? void 0 : _b.hidden),
					subText: ((_c = q('[data-sub]')) === null || _c === void 0 ? void 0 : _c.textContent) || '',
					totalText: ((_d = q('[data-total]')) === null || _d === void 0 ? void 0 : _d.textContent) || '',
					compareText: ((_e = q('[data-compare]')) === null || _e === void 0 ? void 0 : _e.textContent) || '',
					compareHidden: Boolean((_f = q('[data-compare]')) === null || _f === void 0 ? void 0 : _f.hidden),
					noteText: ((_g = q('[data-note]')) === null || _g === void 0 ? void 0 : _g.textContent) || '',
					noteHidden: q('[data-note]') ? Boolean(q('[data-note]').hidden) : true,
				},
			};
		}
		get isSubscribe() {
			return this.state.purchase === 'sub';
		}
		get currentRow() {
			return this.state.row;
		}
		rowData(row = this.currentRow) {
			let dataset = {};
			if (row && row.dataset) {
				dataset = row.dataset;
			}
			return {
				qty: Math.max(1, toInt(dataset.qty, 1)),
				variantId: dataset.variantId || '',
				subPrice: toInt(dataset.subPrice),
				subCompareAt: toInt(dataset.subCompareAt),
				oncePrice: toInt(dataset.oncePrice),
				supplyName: dataset.supplyName || '',
				cadenceText: dataset.cadenceText || dataset.delivery || dataset.cadence || '',
				nextOrder: dataset.nextOrder || '',
				deliveries: dataset.deliveries || '',
				sellingPlanId: dataset.sellingPlanId || dataset.planId || dataset.sellingPlan || '',
				available: this.isVariantAvailable(row),
			};
		}
		isVariantAvailable(row) {
			if (!row) return false;
			const variantId = row.dataset.variantId;
			const option = variantId ? this.variantOptions.get(String(variantId)) : null;
			if (option) {
				return !option.disabled && !option.classList.contains('is--sold-out');
			}
			return row.dataset.available !== 'false';
		}
		listen(target, type, handler, options) {
			if (!target || !target.addEventListener) return;
			target.addEventListener(type, handler, options || false);
			this.cleanupFns.push(() => {
				target.removeEventListener(type, handler, options || false);
			});
		}
		bind() {
			this.listen(this.root, 'click', (event) => {
				const target = event.target && event.target.closest ? event.target : null;
				const actionEl = target ? target.closest('[data-action]') : null;
				if (!actionEl || !this.root.contains(actionEl)) return;
				this.handleAction(actionEl, event);
			});
			if (this.cadenceList) {
				this.listen(this.cadenceList, 'change', (event) => {
					const target = event.target && event.target.closest ? event.target : null;
					const control = target ? target.closest('input, select') : null;
					if (!control) return;
					this.state.deliverySource = 'control';
					this.syncSellingPlan(control);
					this.renderDelivery();
					this.renderCta();
				});
			}
			if (this.cta) {
				this.listen(this.cta, 'click', () => {
					this.syncForm();
					this.syncStickyLoading();
				});
			}
			if (this.ui.stickyCta) {
				this.listen(this.ui.stickyCta, 'click', (event) => {
					event.preventDefault();
					if (this.cta && !this.cta.disabled) this.cta.click();
				});
			}
		}
		handleAction(actionEl, event) {
			const { action } = actionEl.dataset;
			switch (action) {
				case 'draft-purchase': {
					const purchase = actionEl.dataset.value;
					if (!purchase || purchase === this.state.purchase) return;
					this.spin();
					this.state.purchase = purchase;
					this.state.editingCadence = false;
					this.render();
					this.syncForm();
					break;
				}
				case 'qty': {
					const row = actionEl.closest('.bb-row');
					this.selectRow(row);
					break;
				}
				case 'variant': {
					const variantId = String(actionEl.dataset.variantId || '');
					const row = this.rows.find((item) => String(item.dataset.variantId || '') === variantId);
					this.selectRow(row);
					break;
				}
				case 'nudge':
					this.spin();
					this.state.purchase = 'sub';
					this.state.editingCadence = false;
					this.render();
					this.syncForm();
					break;
				case 'toggle-cadence':
					this.state.editingCadence = !this.state.editingCadence;
					this.renderDelivery();
					break;
				case 'open-cancel':
					this.openCancel();
					break;
				case 'use-recommended':
					this.useRecommendedCadence();
					break;
				case 'draft-cadence':
					this.requestFrame(() => {
						this.state.deliverySource = 'control';
						this.markCadenceControl(actionEl);
						this.syncSellingPlan(actionEl);
						this.renderDelivery();
						this.renderCta();
					});
					break;
				default:
					break;
			}
		}
		selectRow(row) {
			if (!row || row === this.currentRow || !this.isVariantAvailable(row)) return;
			this.state.row = row;
			this.state.editingCadence = false;
			this.state.deliverySource = 'row';
			this.syncCadenceListFromRow(row);
			this.render();
			this.syncForm();
		}
		render() {
			this.root.dataset.purchase = this.state.purchase;
			this.form.dataset.purchase = this.state.purchase;
			this.renderPurchaseSwitch();
			this.renderVariantPicker();
			this.renderRows();
			this.renderHead();
			this.renderNudge();
			this.renderDelivery();
			this.renderDoctor();
			this.renderCta();
		}
		renderPurchaseSwitch() {
			this.ui.purchaseButtons.forEach((button) => {
				button.setAttribute('aria-checked', String(button.dataset.value === this.state.purchase));
			});
		}
		renderVariantPicker() {
			const currentVariantId = String(this.rowData().variantId || '');
			this.ui.variantButtons.forEach((button) => {
				const selected = String(button.dataset.variantId || '') === currentVariantId;
				button.classList.toggle('is-selected', selected);
				button.setAttribute('aria-checked', String(selected));
			});
		}
		renderRows() {
			const subscribe = this.isSubscribe;
			this.rows.forEach((row) => {
				const cache = this.rowCache.get(row);
				const data = this.rowData(row);
				const selected = row === this.currentRow;
				const available = data.available;
				const { original } = cache;
				row.classList.toggle('is-selected', selected);
				row.classList.toggle('is-unavailable', !available);
				if (cache.button) {
					cache.button.disabled = !available;
					cache.button.setAttribute('aria-checked', String(selected));
					cache.button.setAttribute('aria-disabled', String(!available));
				}
				setHidden(cache.details, !subscribe);
				if (subscribe) {
					if (cache.save) {
						setHidden(cache.save, data.subCompareAt <= data.subPrice);
						setText(cache.save, original.saveText);
					}
					if (cache.sub) {
						setText(cache.sub, cache.sub.dataset.subscriptionText || original.subText);
					}
					setText(cache.total, this.money(data.subPrice, original.totalText));
					if (cache.compare) {
						const hidden = data.subCompareAt <= data.subPrice;
						setHidden(cache.compare, hidden);
						if (!hidden) {
							setText(cache.compare, this.money(data.subCompareAt, original.compareText || original.totalText));
						}
					}
					if (cache.note) {
						setHidden(cache.note, original.noteHidden);
						setText(cache.note, original.noteText);
					}
					return;
				}
				setHidden(cache.save, true);
				setHidden(cache.compare, true);
				setHidden(cache.note, true);
				setText(cache.total, this.money(data.oncePrice, original.totalText));
				if (cache.sub) {
					const copy = data.qty > 1 ? `${data.qty}-bottle pack · ${this.money(Math.round(data.oncePrice / data.qty), original.totalText)}/bottle` : 'One-time purchase · try it first';
					setText(cache.sub, copy);
				}
			});
		}
		renderHead() {
			setHidden(this.ui.hintWrap, !this.isSubscribe);
			if (!this.ui.hint || !this.isSubscribe) return;
			const data = this.rowData();
			const saving = Math.max(0, data.subCompareAt - data.subPrice);
			setText(this.ui.hint, this.money(saving, this.currentMoneySample()));
		}
		renderNudge() {
			const nudge = this.ui.nudge;
			if (!nudge) return;
			const data = this.rowData();
			const saving = Math.max(0, data.oncePrice - data.subPrice);
			const show = !this.isSubscribe && data.subPrice > 0 && saving > 0;
			const shippingWarning = nudge.querySelector('[data-nudge-ship]');
			setHidden(nudge, !show);
			setHidden(shippingWarning, true);
			if (!show) return;
			const sample = this.currentMoneySample();
			setText(nudge.querySelector('[data-nudge-title]'), `Subscribers pay ${this.money(data.subPrice, sample)} for this order`);
			setText(nudge.querySelector('[data-nudge-cta]'), `Switch & save ${this.money(saving, sample)} →`);
			if (!shippingWarning) return;
			const threshold = Math.max(0, toInt(this.root.dataset.freeShipThreshold, 9000));
			const missesFreeShipping = threshold > 0 && data.oncePrice < threshold;
			setHidden(shippingWarning, !missesFreeShipping);
			if (!missesFreeShipping || shippingWarning.dataset.bbRendered === 'true') return;
			shippingWarning.innerHTML = `<svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true"><circle cx="6" cy="6" r="5.2" stroke="#98A296" stroke-width="1.4"></circle><path d="M4 4l4 4M8 4l-4 4" stroke="#98A296" stroke-width="1.5" stroke-linecap="round"></path></svg><span>You're missing out on free shipping</span>`;
			shippingWarning.dataset.bbRendered = 'true';
		}
		renderDelivery() {
			if (!this.delivery || !this.deliveryUi) return;
			setHidden(this.delivery, !this.isSubscribe);
			if (!this.isSubscribe) return;
			const controls = this.cadenceControls();
			const canEdit = controls.length >= 2;
			if (this.deliveryUi.toggle) {
				setText(this.deliveryUi.toggle, this.state.editingCadence ? 'Done' : 'Adjust');
				setHidden(this.deliveryUi.toggle, !canEdit);
				this.deliveryUi.toggle.setAttribute('aria-expanded', String(this.state.editingCadence));
			}
			setHidden(this.cadenceList, !this.state.editingCadence || !canEdit);
			const data = this.rowData();
			if (this.state.deliverySource === 'row') {
				if (data.cadenceText) {
					setText(this.deliveryUi.cadenceStrong, data.cadenceText.toLowerCase());
				}
				setHidden(this.deliveryUi.next, this.state.editingCadence);
				if (data.nextOrder && this.deliveryUi.next) {
					const deliveries = data.deliveries ? ` · ${data.deliveries}` : '';
					setText(this.deliveryUi.next, `Next order ${data.nextOrder}${deliveries}`);
				}
				this.renderMismatch();
				return;
			}
			const control = this.selectedCadenceControl();
			const label = this.cadenceLabel(control) || data.cadenceText || '';
			const cadence = parseCadence(label);
			if (label) setText(this.deliveryUi.cadenceStrong, label.toLowerCase());
			if (cadence) {
				const nextDate = nextDateFromCadence(cadence);
				const perYear = deliveriesPerYear(cadence);
				const deliveries = perYear ? ` · ${perYear} ${perYear === 1 ? 'delivery' : 'deliveries'} a year` : '';
				setHidden(this.deliveryUi.next, this.state.editingCadence);
				if (nextDate) {
					setText(this.deliveryUi.next, `Next order ${formatDate(nextDate)}${deliveries}`);
				}
			} else {
				setHidden(this.deliveryUi.next, this.state.editingCadence);
			}
			this.renderMismatch();
		}
		renderMismatch() {
			var _a;
			const mismatch = (_a = this.deliveryUi) === null || _a === void 0 ? void 0 : _a.mismatch;
			if (!mismatch) return;
			const data = this.rowData();
			const recommendedPlanId = normalizePlanId(data.sellingPlanId);
			const selected = this.selectedCadenceControl();
			const selectedPlanId = this.planIdFromControl(selected);
			const show = this.isSubscribe && this.state.deliverySource === 'control' && Boolean(recommendedPlanId) && Boolean(selectedPlanId) && selectedPlanId !== recommendedPlanId;
			setHidden(mismatch, !show);
			if (!show) return;
			const recommendedCadence = parseCadence(data.cadenceText);
			const selectedCadence = parseCadence(this.cadenceLabel(selected));
			const supplyLabel = this.supplyLabel(recommendedCadence);
			let message = supplyLabel ? `This is a ${supplyLabel} — this schedule differs from the recommended timing.` : 'This delivery schedule differs from the recommended timing.';
			const recommendedDays = cadenceDays(recommendedCadence);
			const selectedDays = cadenceDays(selectedCadence);
			if (supplyLabel && recommendedDays && selectedDays) {
				if (selectedDays < recommendedDays) {
					message = `This is a ${supplyLabel} — you may get more than you need.`;
				} else if (selectedDays > recommendedDays) {
					message = `This is a ${supplyLabel} — you may run out before your next delivery.`;
				}
			}
			let text = mismatch.querySelector('[data-mismatch-text]');
			let button = mismatch.querySelector('[data-action="use-recommended"]');
			if (!text || !button) {
				replaceChildrenCompat(mismatch);
				text = document.createElement('span');
				text.dataset.mismatchText = '';
				button = document.createElement('button');
				button.type = 'button';
				button.className = 'bb-link';
				button.dataset.action = 'use-recommended';
				button.textContent = 'Use recommended';
				mismatch.appendChild(text);
				mismatch.appendChild(button);
			}
			setText(text, message);
		}
		renderDoctor() {
			setHidden(this.ui.doctor, !this.isSubscribe);
		}
		renderCta() {
			var _a, _b;
			const data = this.rowData();
			const rowPlanId = normalizePlanId(data.sellingPlanId);
			const selectedPlanId = this.planIdFromControl(this.selectedCadenceControl());
			const planId = this.isSubscribe ? (this.state.deliverySource === 'row' ? rowPlanId : '') || selectedPlanId || this.lastPlanId || this.firstPlanId() : '';
			const available = data.available && (!this.isSubscribe || Boolean(planId));
			const labelKey = this.isSubscribe ? 'ctaLabelSub' : 'ctaLabelOnetime';
			const label = ((_b = (_a = this.cta) === null || _a === void 0 ? void 0 : _a.dataset) === null || _b === void 0 ? void 0 : _b[labelKey]) || '';
			const price = this.isSubscribe ? data.subPrice : data.oncePrice;
			const comparePrice = this.isSubscribe ? data.subCompareAt : 0;
			const sample = this.currentMoneySample();
			if (this.cta && this.ctaUi) {
				setText(this.ctaUi.label, available ? label : 'Out of stock');
				setHidden(this.ctaUi.separator, !available);
				const hideCompare = !available || comparePrice <= price;
				setHidden(this.ctaUi.compare, hideCompare);
				if (!hideCompare) {
					setText(this.ctaUi.compare, this.money(comparePrice, sample));
				}
				if (this.ctaUi.total && this.ctaUi.total !== this.ctaUi.label) {
					setHidden(this.ctaUi.total, !available);
					if (available) setText(this.ctaUi.total, this.money(price, sample));
				}
			}
			if (this.ui.disclosure && this.isSubscribe) {
				const labelText = (this.state.deliverySource === 'row' ? data.cadenceText : '') || this.cadenceLabel(this.selectedCadenceControl());
				if (labelText) {
					setText(this.ui.disclosure, `Renews ${labelText.toLowerCase()} until you cancel.`);
				}
			}
			this.renderSticky({
				available,
				label,
				price,
				supplyName: data.supplyName,
			});
		}
		renderSticky({ available, label, price, supplyName }) {
			if (!this.stickyUi) return;
			const sample = this.currentMoneySample();
			setText(this.stickyUi.total, this.money(price, sample));
			setText(this.stickyUi.sub, `${supplyName} · ${this.isSubscribe ? 'cancel anytime' : 'one-time'}`);
			setText(this.stickyUi.label, available ? label : 'Out of stock');
			if (this.stickyUi.button) this.stickyUi.button.disabled = !available;
		}
		currentMoneySample() {
			const cache = this.rowCache.get(this.currentRow);
			return (cache && cache.total && cache.total.textContent) || (cache && cache.original && cache.original.totalText) || '';
		}
		useRecommendedCadence() {
			const planId = normalizePlanId(this.rowData().sellingPlanId);
			if (!planId) return;
			this.selectCadencePlan(planId);
			this.lastPlanId = planId;
			this.state.deliverySource = 'row';
			this.state.editingCadence = false;
			this.syncSellingPlan();
			this.renderDelivery();
			this.renderCta();
		}
		supplyLabel(cadence) {
			if (!cadence) return '';
			return `${cadence.count}-${cadence.unit} supply`;
		}
		syncForm() {
			const data = this.rowData();
			if (!data.variantId) return;
			if (this.qtyInput) this.qtyInput.value = '1';
			if (this.select && String(this.select.value) !== String(data.variantId)) {
				this.select.value = String(data.variantId);
				this.select.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
			}
			if (this.isSubscribe) {
				this.syncSellingPlan();
			} else if (this.planInput) {
				if (this.planInput.value) this.lastPlanId = this.planInput.value;
				this.planInput.value = '';
				this.planInput.disabled = true;
			}
			this.updateQuickShopLinks();
		}
		syncSellingPlan(control = null) {
			if (!this.planInput) return;
			const selected = control || this.selectedCadenceControl();
			const rowPlanId = this.state.deliverySource === 'row' ? normalizePlanId(this.rowData().sellingPlanId) : '';
			const planId = rowPlanId || this.planIdFromControl(selected) || this.lastPlanId || this.firstPlanId();
			if (planId) this.lastPlanId = planId;
			this.planInput.value = planId || '';
			this.planInput.disabled = !planId || !this.isSubscribe;
			this.updateQuickShopLinks();
		}
		syncCadenceListFromRow(row = this.currentRow) {
			var _a;
			if (!row || !this.cadenceList) return;
			const template = (_a = this.rowCache.get(row)) === null || _a === void 0 ? void 0 : _a.cadenceTemplate;
			if (!template || !('content' in template)) return;
			const wasHidden = this.cadenceList.hidden;
			replaceChildrenCompat(this.cadenceList, template.content.cloneNode(true));
			this.cadenceList.hidden = wasHidden;
			const planId = normalizePlanId(this.rowData(row).sellingPlanId);
			if (!planId) {
				if (this.planInput) {
					this.planInput.value = '';
					this.planInput.disabled = true;
				}
				return;
			}
			this.lastPlanId = planId;
			this.selectCadencePlan(planId);
			if (this.planInput) {
				this.planInput.value = planId;
				this.planInput.disabled = !this.isSubscribe;
			}
		}
		cadenceControls() {
			return this.cadenceList ? Array.from(this.cadenceList.querySelectorAll(CADENCE_CONTROL_SELECTOR)) : [];
		}
		selectedCadenceControl() {
			if (!this.cadenceList) {
				return null;
			}
			const select = this.cadenceList.querySelector('select');
			if (select && select.selectedOptions && select.selectedOptions[0]) {
				return select.selectedOptions[0];
			}
			return this.cadenceList.querySelector('input[type="radio"]:checked') || this.cadenceList.querySelector('[data-action="draft-cadence"][aria-checked="true"], ' + '[data-selling-plan-id][aria-checked="true"], ' + '[data-plan-id][aria-checked="true"], ' + '.is-selected[data-action="draft-cadence"]') || this.cadenceControls()[0] || null;
		}
		markCadenceControl(control) {
			if (!control || !this.cadenceList) return;
			this.cadenceList.querySelectorAll('[data-action="draft-cadence"]').forEach((button) => {
				const selected = button === control;
				button.setAttribute('aria-checked', String(selected));
				button.classList.toggle('is-selected', selected);
			});
		}
		selectCadencePlan(planId) {
			if (!planId || !this.cadenceList) return;
			const normalized = normalizePlanId(planId);
			if (!normalized) return;
			this.cadenceList.querySelectorAll('select').forEach((select) => {
				const option = Array.from(select.options).find((item) => normalizePlanId(item.value) === normalized || this.planIdFromControl(item) === normalized);
				if (option) select.value = option.value;
			});
			this.cadenceList.querySelectorAll('input[type="radio"]').forEach((input) => {
				const id = this.planIdFromControl(input);
				if (id) input.checked = id === normalized;
			});
			this.cadenceList.querySelectorAll('[data-action="draft-cadence"], [data-selling-plan-id], [data-plan-id], [data-selling-plan]').forEach((control) => {
				const id = this.planIdFromControl(control);
				if (!id) return;
				const selected = id === normalized;
				control.setAttribute('aria-checked', String(selected));
				control.classList.toggle('is-selected', selected);
			});
		}
		cadenceLabel(control) {
			if (!control) {
				return '';
			}
			const dataset = control.dataset || {};
			const explicit = dataset.delivery || dataset.cadenceLabel || dataset.label || '';
			if (explicit) {
				return String(explicit).trim();
			}
			let nestedLabel = null;
			if (typeof control.querySelector === 'function') {
				nestedLabel = control.querySelector('.nb-cadence__lbl, [data-cadence-label]');
			}
			if (nestedLabel && nestedLabel.textContent) {
				return nestedLabel.textContent.trim();
			}
			if (typeof control.matches === 'function' && control.matches('option')) {
				return control.textContent ? control.textContent.trim() : '';
			}
			if (typeof control.matches === 'function' && control.matches('input')) {
				let label = null;
				if (control.id) {
					label = this.cadenceList.querySelector(`label[for="${escapeCssValue(control.id)}"]`);
				} else if (typeof control.closest === 'function') {
					label = control.closest('label');
				}
				if (label && label.textContent) {
					return label.textContent.trim();
				}
			}
			let ariaLabel = '';
			let title = '';
			if (typeof control.getAttribute === 'function') {
				ariaLabel = control.getAttribute('aria-label') || '';
				title = control.getAttribute('title') || '';
			}
			return (ariaLabel || title || control.textContent || '').trim();
		}
		planIdFromControl(control) {
			if (!control) {
				return '';
			}
			const dataset = control.dataset || {};
			const datasetId = dataset.sellingPlanId || dataset.planId || dataset.sellingPlan || '';
			if (datasetId) {
				return normalizePlanId(datasetId);
			}
			if (typeof control.matches === 'function' && control.matches('select, input')) {
				const name = control.name || '';
				if (name.indexOf('selling_plan') !== -1) {
					return normalizePlanId(control.value);
				}
			}
			if (typeof control.matches === 'function' && control.matches('option')) {
				let select = null;
				if (typeof control.closest === 'function') {
					select = control.closest('select');
				}
				if (select) {
					const selectName = select.name || '';
					if (selectName.indexOf('selling_plan') !== -1) {
						return normalizePlanId(control.value);
					}
				}
			}
			let nested = null;
			if (typeof control.querySelector === 'function') {
				nested = control.querySelector('[data-selling-plan-id], ' + '[data-plan-id], ' + '[data-selling-plan], ' + 'input[name*="selling_plan"]');
			}
			if (nested) {
				return this.planIdFromControl(nested);
			}
			let attributeValue = '';
			if (typeof control.getAttribute === 'function') {
				attributeValue = control.getAttribute('value') || '';
			}
			const datasetValue = dataset.value || '';
			return normalizePlanId(attributeValue || datasetValue || '');
		}
		firstPlanId() {
			for (const control of this.cadenceControls()) {
				const planId = this.planIdFromControl(control);
				if (planId) return planId;
			}
			return '';
		}
		money(cents, sample = '') {
			var _a, _b, _c;
			try {
				if ((_a = window.Shopify) === null || _a === void 0 ? void 0 : _a.formatMoney) {
					const formatted = window.Shopify.formatMoney(cents);
					if (formatted) return String(formatted).replace(/<[^>]+>/g, '');
				}
			} catch (_) {
				// Fall through to local formatting.
			}
			const source = String(sample || '').trim();
			const numeric = ((_c = (_b = source.match(/[\d][\d,.\s]*/)) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.trim()) || '';
			const index = numeric ? source.indexOf(numeric) : -1;
			const prefix = index >= 0 ? source.slice(0, index) : '$';
			const suffix = index >= 0 ? source.slice(index + numeric.length) : '';
			const hasDecimals = /[.,]\d{2}\b/.test(numeric);
			const value = (cents || 0) / 100;
			const formatted = value.toLocaleString('en-US', {
				minimumFractionDigits: hasDecimals ? 2 : 0,
				maximumFractionDigits: 2,
			});
			return `${prefix}${formatted}${suffix}`.trim();
		}
		updateQuickShopLinks() {
			var _a;
			if (!this.quickShopRoot || !this.form.dataset.url) return;
			const url = new URL(this.form.dataset.url, window.location.origin);
			const variantId = (_a = this.select) === null || _a === void 0 ? void 0 : _a.value;
			if (variantId) url.searchParams.set('variant', variantId);
			if (this.isSubscribe && this.planInput && !this.planInput.disabled && this.planInput.value) {
				url.searchParams.set('selling_plan', this.planInput.value);
			} else {
				url.searchParams.delete('selling_plan');
			}
			this.root.querySelectorAll('[data-qs-pdp]').forEach((link) => {
				if (link.href !== url.href) link.href = url.href;
			});
		}
		bindQuickShop() {
			const dragTarget = this.root.querySelector('.nbq__head');
			const drawer = this.root.closest('#t4s-quickshop-drawer');
			if (!drawer) return;
			const media = window.matchMedia(MOBILE_QUERY);
			const viewport = window.visualViewport;
			let drag = null;
			let viewportFrame = 0;
			const syncViewport = () => {
				viewportFrame = 0;
				if (media.matches) {
					drawer.style.removeProperty('height');
					drawer.style.removeProperty('max-height');
					drawer.style.removeProperty('bottom');
					return;
				}
				let visibleHeight = window.innerHeight;
				if (viewport && viewport.height) {
					visibleHeight = viewport.height;
				}
				const bottomInset = viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0;
				const topGap = Math.max(16, Math.round(visibleHeight * 0.06));
				const height = Math.max(320, Math.floor(visibleHeight - topGap));
				const px = `${height}px`;
				const bottom = `${bottomInset}px`;
				if (drawer.style.height !== px) {
					drawer.style.height = px;
				}
				if (drawer.style.maxHeight !== px) {
					drawer.style.maxHeight = px;
				}
				if (drawer.style.bottom !== bottom) {
					drawer.style.bottom = bottom;
				}
			};
			const scheduleViewportSync = () => {
				if (viewportFrame) return;
				viewportFrame = requestAnimationFrame(syncViewport);
			};
			scheduleViewportSync();
			this.listen(window, 'resize', scheduleViewportSync, { passive: true });
			this.listen(window, 'orientationchange', scheduleViewportSync, { passive: true });
			if (media.addEventListener) {
				this.listen(media, 'change', scheduleViewportSync);
			} else if (media.addListener) {
				media.addListener(scheduleViewportSync);
				this.cleanupFns.push(() => media.removeListener(scheduleViewportSync));
			}
			if (viewport) {
				this.listen(viewport, 'resize', scheduleViewportSync, { passive: true });
				this.listen(viewport, 'scroll', scheduleViewportSync, { passive: true });
			}
			const resetDrawer = () => {
				drawer.style.transition = '';
				drawer.style.transform = '';
			};
			const closeQuickShop = () => {
				resetDrawer();
				const closeButton = this.root.querySelector('[data-drawer-close]');
				if (closeButton) closeButton.click();
			};
			if (dragTarget) {
				const beginDrag = (clientY, pointerId) => {
					drag = {
						pointerId: pointerId,
						startY: clientY,
						distance: 0,
						startedAt: Date.now(),
					};
					drawer.style.transition = 'none';
				};
				const moveDrag = (clientY) => {
					if (!drag) return;
					drag.distance = clientY - drag.startY;
					if (drag.distance < -28) drawer.classList.add('nbq-is-expanded');
					drawer.style.transform = `translate3d(0, ${Math.max(0, drag.distance)}px, 0)`;
				};
				const finishDrag = (event) => {
					if (!drag) return;
					const elapsed = Math.max(1, Date.now() - drag.startedAt);
					const shouldClose = drag.distance > 100 || (drag.distance > 28 && drag.distance / elapsed > 0.55);
					drag = null;
					if (shouldClose) {
						if (event && event.preventDefault) event.preventDefault();
						closeQuickShop();
					} else {
						resetDrawer();
					}
				};
				if (window.PointerEvent) {
					this.listen(dragTarget, 'pointerdown', (event) => {
						if (media.matches || event.pointerType === 'mouse') return;
						beginDrag(event.clientY, event.pointerId);
						if (dragTarget.setPointerCapture) {
							try {
								dragTarget.setPointerCapture(event.pointerId);
							} catch (_) {}
						}
					});
					this.listen(dragTarget, 'pointermove', (event) => {
						if (!drag || event.pointerId !== drag.pointerId) return;
						moveDrag(event.clientY);
					});
					this.listen(dragTarget, 'pointerup', (event) => {
						if (!drag || event.pointerId !== drag.pointerId) return;
						finishDrag(event);
					});
					this.listen(dragTarget, 'pointercancel', (event) => {
						if (!drag || event.pointerId !== drag.pointerId) return;
						finishDrag(event);
					});
				} else {
					this.listen(
						dragTarget,
						'touchstart',
						(event) => {
							if (media.matches || !event.touches || event.touches.length !== 1) return;
							const touch = event.touches[0];
							beginDrag(touch.clientY, touch.identifier);
						},
						{ passive: true },
					);
					this.listen(
						dragTarget,
						'touchmove',
						(event) => {
							if (!drag || !event.touches) return;
							let touch = null;
							for (let i = 0; i < event.touches.length; i += 1) {
								if (event.touches[i].identifier === drag.pointerId) {
									touch = event.touches[i];
									break;
								}
							}
							if (touch) moveDrag(touch.clientY);
						},
						{ passive: true },
					);
					this.listen(dragTarget, 'touchend', (event) => finishDrag(event));
					this.listen(dragTarget, 'touchcancel', (event) => finishDrag(event));
				}
			}
			this.listen(this.root, 'keydown', (event) => {
				if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
					return;
				}
				const radio = event.target.closest('button[role="radio"]');
				let group = null;
				if (radio && typeof radio.closest === 'function') {
					group = radio.closest('[role="radiogroup"]');
				}
				if (!group) return;
				const buttons = Array.from(group.querySelectorAll('button[role="radio"]:not(:disabled)')).filter((button) => button.getClientRects().length);
				if (!buttons.length) return;
				const step = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
				const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (buttons.indexOf(radio) + step + buttons.length) % buttons.length;
				event.preventDefault();
				buttons[index].click();
				buttons[index].focus();
			});
			this.updateQuickShopLinks();
		}
		enableStickyTracking() {
			if (this.stickyTrackingStarted || !this.cta || !this.sticky) return;
			this.stickyTrackingStarted = true;
			let lastScrollY = window.scrollY;
			let scrollingUp = false;
			let frame = 0;
			const update = () => {
				frame = 0;
				const currentScrollY = window.scrollY;
				const delta = currentScrollY - lastScrollY;
				if (Math.abs(delta) > 2) scrollingUp = delta < 0;
				const ctaAboveViewport = this.cta.getBoundingClientRect().bottom <= 0;
				const shouldShow = ctaAboveViewport && scrollingUp;
				if (this.sticky.classList.contains('is-visible') !== shouldShow) {
					this.sticky.classList.toggle('is-visible', shouldShow);
				}
				lastScrollY = currentScrollY;
			};
			const schedule = () => {
				if (frame) return;
				frame = requestAnimationFrame(update);
			};
			this.listen(window, 'scroll', schedule, { passive: true });
			this.listen(window, 'resize', schedule, { passive: true });
			this.sticky.classList.remove('is-visible');
		}
		syncStickyLoading() {
			var _a, _b;
			if (!this.cta || !((_a = this.stickyUi) === null || _a === void 0 ? void 0 : _a.button)) return;
			(_b = this.stickyLoadingObserver) === null || _b === void 0 ? void 0 : _b.disconnect();
			if (this.stickyLoadingTimer) clearTimeout(this.stickyLoadingTimer);
			let sawLoading = false;
			const stickyButton = this.stickyUi.button;
			const cleanup = () => {
				var _a;
				(_a = this.stickyLoadingObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
				this.stickyLoadingObserver = null;
				if (this.stickyLoadingTimer) clearTimeout(this.stickyLoadingTimer);
				this.stickyLoadingTimer = 0;
			};
			const sync = () => {
				const loading = this.cta.classList.contains('is--loading') || this.cta.classList.contains('nb-atc-busy') || this.cta.getAttribute('aria-busy') === 'true';
				stickyButton.classList.toggle('is--loading', loading);
				if (loading) sawLoading = true;
				if (sawLoading && !loading) cleanup();
			};
			this.stickyLoadingObserver = new MutationObserver(sync);
			this.stickyLoadingObserver.observe(this.cta, {
				attributes: true,
				attributeFilter: ['class', 'aria-busy'],
			});
			this.stickyLoadingTimer = window.setTimeout(() => {
				stickyButton.classList.remove('is--loading');
				cleanup();
			}, 10000);
			sync();
		}
		spin() {
			const icon = this.root.querySelector('.bb-switch__icon');
			if (!icon) return;
			if (this.spinFrame) cancelAnimationFrame(this.spinFrame);
			if (this.spinEndHandler) {
				icon.removeEventListener('animationend', this.spinEndHandler);
			}
			icon.classList.remove('is-turning');
			this.spinFrame = requestAnimationFrame(() => {
				this.spinFrame = 0;
				icon.classList.add('is-turning');
				this.spinEndHandler = () => {
					icon.classList.remove('is-turning');
					this.spinEndHandler = null;
				};
				icon.addEventListener('animationend', this.spinEndHandler, {
					once: true,
				});
			});
		}
		openCancel() {
			var _a, _b;
			const sheet = this.root.querySelector('[data-sheet]');
			if (!sheet) return;
			const overlay = document.createElement('div');
			overlay.className = 'bb-overlay';
			overlay.setAttribute('role', 'dialog');
			overlay.setAttribute('aria-modal', 'true');
			overlay.setAttribute('aria-label', 'How cancelling works');
			overlay.innerHTML = sheet.innerHTML;
			const previousFocus = document.activeElement;
			const close = () => {
				overlay.remove();
				document.removeEventListener('keydown', onKeydown);
				if (previousFocus && typeof previousFocus.focus === 'function') {
					previousFocus.focus();
				}
			};
			const onKeydown = (event) => {
				if (event.key === 'Escape') close();
			};
			overlay.addEventListener('click', (event) => {
				if (event.target === overlay || event.target.closest('[data-close]')) {
					close();
				}
			});
			document.addEventListener('keydown', onKeydown);
			document.body.appendChild(overlay);
			(_b = (_a = overlay.querySelector('[data-close]')) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
		}
		requestFrame(callback) {
			const id = requestAnimationFrame(() => {
				this.rafIds.delete(id);
				callback();
			});
			this.rafIds.add(id);
			return id;
		}
		destroy() {
			var _a;
			this.cleanupFns.forEach((cleanup) => {
				try {
					cleanup();
				} catch (_) {}
			});
			this.cleanupFns.length = 0;
			(_a = this.stickyLoadingObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
			if (this.stickyLoadingTimer) clearTimeout(this.stickyLoadingTimer);
			if (this.spinFrame) cancelAnimationFrame(this.spinFrame);
			this.rafIds.forEach((id) => cancelAnimationFrame(id));
			this.rafIds.clear();
			this.timeouts.forEach((id) => clearTimeout(id));
			this.timeouts.clear();
			delete this.root.__nubestBuyboxV3;
		}
	}
	function boot(scope = document) {
		var _a;
		const roots = [...(((_a = scope.matches) === null || _a === void 0 ? void 0 : _a.call(scope, ROOT_SELECTOR)) ? [scope] : []), ...scope.querySelectorAll(ROOT_SELECTOR)];
		roots.forEach((root) => {
			if (root.__nubestBuyboxV3) return;
			root.__nubestBuyboxV3 = new NuBestBuybox(root);
		});
	}
	function destroy(scope) {
		var _a;
		const roots = [...(((_a = scope.matches) === null || _a === void 0 ? void 0 : _a.call(scope, ROOT_SELECTOR)) ? [scope] : []), ...scope.querySelectorAll(ROOT_SELECTOR)];
		roots.forEach((root) => {
			var _a, _b;
			return (_b = (_a = root.__nubestBuyboxV3) === null || _a === void 0 ? void 0 : _a.destroy) === null || _b === void 0 ? void 0 : _b.call(_a);
		});
	}
	window.NuBestBuybox = window.NuBestBuybox || {};
	window.NuBestBuybox.boot = boot;
	window.NuBestBuybox.destroy = destroy;
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
	} else {
		boot();
	}
	document.addEventListener('shopify:section:load', (event) => boot(event.target));
	document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
